import React, { useEffect, useState } from 'react';
import { Button, Input, Select, notification, Spin } from 'antd';
import { contracts, clientsApi } from '../api';
import { userRole, employeeId } from '../../GetRole.utils';
import StepDrawer from '../other/StepDrawer';
import {
  Field,
  EntityChoice,
  ClientSelect,
  TermFields,
  FileDrop,
  Summary,
  entityName,
  apiErrorMessage,
  parseApiDate,
  formatDate,
} from '../other/contractFields';

const STATUS = { draft: 1, pending: 2 };

// same mobile number rule as Client > Point Of Contact(s)
const phoneRegex =
  /^\+?[0-9]{0,3}\W?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;

const emptyValues = (prefill) => ({
  seId: prefill?.seId ?? 1,
  clientId: prefill?.clientId ?? null,
  clientLabel: prefill?.legalEntityName ?? '',
  start: null,
  end: null,
  endless: false,
  signatoryName: '',
  signatoryEmail: '',
  signatoryMobile: '',
  businessUnit: null,
  businessOwner: null,
});

const labelFilter = (input, option) =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

// Create or edit an MSA in a step drawer.
// contractId + status: edit an existing MSA. prefill: lock the client (from the client page).
const MsaDrawer = ({ open, onClose, contractId, status, prefill }) => {
  const isEdit = !!contractId;
  const [step, setStep] = useState(0);
  const [values, setValues] = useState(emptyValues(prefill));
  const [file, setFile] = useState(null);
  const [existingFileName, setExistingFileName] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);

  const canSendForApproval = userRole !== 'finance' && (!isEdit || status === 'draft');
  // editing a draft: only the contracting entity is disabled, every other field stays enabled
  const isDraftEdit = isEdit && status === 'draft';
  const entityLocked = isEdit || !!prefill?.clientId;
  const clientLocked = isDraftEdit ? false : isEdit || !!prefill?.clientId;

  useEffect(() => {
    clientsApi
      .getEmployeeName()
      .then((res) => setEmployees((res.data || []).map((e) => ({ value: e.empCode, label: e.name }))))
      .catch((err) => console.error('Error fetching employees:', err));
    contracts
      .getBusinessUnitData()
      .then((res) =>
        setBusinessUnits(
          (res.data || []).map((b) => ({ value: b.businessUnitId, label: b.businessUnitName }))
        )
      )
      .catch((err) => console.error('Error fetching business units:', err));
  }, []);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setErrors({});
    setFile(null);
    setExistingFileName('');
    setValues(emptyValues(prefill));
    if (!isEdit) return;

    setLoading(true);
    contracts
      .getContractsData(contractId)
      .then((res) => {
        const data = res.data[0]?.contractData || {};
        setValues({
          seId: Number(data.seId),
          clientId: data.client?.clientId ?? null,
          clientLabel: data.client?.legalEntityName ?? '',
          start: parseApiDate(data.activationDate),
          end: parseApiDate(data.expiryDate),
          endless: data.expiryDate == null,
          signatoryName: data.clientAuthorizerName || '',
          signatoryEmail: data.clientAuthorizerEmail || '',
          signatoryMobile: data.clientAuthorizerMobile || '',
          businessUnit: data.spanideaBusinessUnit?.businessUnitId ?? null,
          businessOwner: data.spanideaAuthorizerEmpName?.empCode ?? null,
        });
        setExistingFileName(data.fileName || '');
      })
      .catch((err) => console.error('Error fetching MSA:', err))
      .finally(() => setLoading(false));
    // prefill is read once when the drawer opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractId]);

  const update = (patch) => {
    setValues((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => delete next[key]);
      if (patch.end !== undefined || patch.endless !== undefined) delete next.end;
      return next;
    });
  };

  const stepErrors = (index) => {
    const e = {};
    if (index === 0) {
      if (!values.clientId) e.clientId = 'Choose a client';
      if (!values.start) e.start = 'Choose a start date';
      if (!values.endless && !values.end) e.end = 'Choose an end date, or tick "No end date"';
    }
    if (index === 1) {
      if (!values.signatoryName.trim()) e.signatoryName = 'Enter the signatory name';
      if (!values.signatoryEmail) e.signatoryEmail = 'Enter the signatory email';
      else if (!/^\S+@\S+\.\S+$/.test(values.signatoryEmail))
        e.signatoryEmail = 'Enter a valid email address (e.g. name@company.com)';
      if (!values.signatoryMobile) e.signatoryMobile = 'Enter the signatory mobile number';
      else if (!phoneRegex.test(values.signatoryMobile.trim()))
        e.signatoryMobile = 'Enter a valid mobile number';
      if (!values.businessUnit) e.businessUnit = 'Choose a business unit';
      if (!values.businessOwner) e.businessOwner = 'Choose a business owner';
    }
    if (index === 2 && !file && !existingFileName) e.file = 'Upload the signed MSA';
    return e;
  };

  const goNext = () => {
    const e = stepErrors(step);
    setErrors(e);
    if (Object.keys(e).length === 0) setStep(step + 1);
  };

  const goToStep = (index) => {
    if (index <= step) {
      setErrors({});
      setStep(index);
      return;
    }
    // jumping forward: validate every step in between, land on the first invalid one
    for (let i = step; i < index; i++) {
      const e = stepErrors(i);
      if (Object.keys(e).length) {
        setErrors(e);
        setStep(i);
        return;
      }
    }
    setErrors({});
    setStep(index);
  };

  const save = (cStatusId) => {
    if (cStatusId === STATUS.pending) {
      for (let i = 0; i < 3; i++) {
        const e = stepErrors(i);
        if (Object.keys(e).length) {
          setErrors(e);
          setStep(i);
          return;
        }
      }
    }

    const obj = {
      seId: values.seId,
      clientId: values.clientId,
      cStatusId,
      contractTypeId: 1,
      activationDate: values.start,
      createdBy: employeeId,
    };
    if (!values.endless && values.end) obj.expiryDate = values.end;
    if (values.signatoryName) obj.clientAuthorizerName = values.signatoryName;
    if (values.signatoryEmail) obj.clientAuthorizerEmail = values.signatoryEmail;
    if (values.signatoryMobile) obj.clientAuthorizerMobile = values.signatoryMobile;
    if (values.businessUnit) obj.spanideaBusinessUnit = values.businessUnit;
    if (values.businessOwner) obj.spanideaAuthorizerEmpCode = values.businessOwner;

    const formdata = new FormData();
    if (file) formdata.append('file', file);
    formdata.append('obj', JSON.stringify(obj));

    setSaving(cStatusId === STATUS.pending ? 'approval' : 'draft');
    const request = isEdit
      ? contracts.updateContractsData(contractId, formdata)
      : contracts.addContractsData(formdata);

    request
      .then((res) => {
        notification.success({
          message:
            cStatusId === STATUS.pending
              ? 'MSA sent for approval'
              : res?.message || 'MSA saved',
        });
        onClose(true);
      })
      .catch((err) => {
        notification.error({
          message: apiErrorMessage(err, 'Could not save the MSA'),
        });
      })
      .finally(() => setSaving(null));
  };

  const ownerLabel = employees.find((e) => e.value === values.businessOwner)?.label;
  const unitLabel = businessUnits.find((b) => b.value === values.businessUnit)?.label;

  const steps = [
    {
      key: 'agreement',
      title: 'Agreement',
      content: loading ? (
        <Spin />
      ) : (
        <div className="sd-form">
          <EntityChoice
            value={values.seId}
            onChange={(seId) => update({ seId, clientId: null, clientLabel: '' })}
            disabled={entityLocked}
          />
          <ClientSelect
            seId={values.seId}
            value={values.clientId}
            onChange={(clientId, option) => update({ clientId, clientLabel: option?.label || '' })}
            disabled={clientLocked}
            currentOption={values.clientId ? { value: values.clientId, label: values.clientLabel } : null}
            error={errors.clientId}
          />
          <TermFields
            start={values.start}
            end={values.end}
            endless={values.endless}
            onChange={update}
            errors={errors}
          />
        </div>
      ),
    },
    {
      key: 'signatories',
      title: 'Signatories',
      content: (
        <div className="sd-form">
          <div className="sd-fieldset">
            <span className="sd-fieldset-title">Client signatory</span>
            <Field label="Name" required error={errors.signatoryName}>
              <Input
                size="large"
                value={values.signatoryName}
                status={errors.signatoryName ? 'error' : ''}
                placeholder="Full name"
                onChange={(e) => {
                  const cleaned = e.target.value
                    .replace(/[^\w\s]/gi, '')
                    .replace(/\b\w/g, (c) => c.toUpperCase());
                  update({ signatoryName: cleaned });
                }}
              />
            </Field>
            <div className="sd-row">
              <Field label="Email" required error={errors.signatoryEmail}>
                <Input
                  size="large"
                  type="email"
                  value={values.signatoryEmail}
                  status={errors.signatoryEmail ? 'error' : ''}
                  placeholder="name@company.com"
                  onChange={(e) => update({ signatoryEmail: e.target.value.trim() })}
                />
              </Field>
              <Field label="Mobile" required error={errors.signatoryMobile}>
                <Input
                  size="large"
                  inputMode="tel"
                  maxLength={20}
                  value={values.signatoryMobile}
                  status={errors.signatoryMobile ? 'error' : ''}
                  placeholder="+91 98765 43210"
                  onChange={(e) => {
                    const { value } = e.target;
                    if (value === '' || /^[0-9+\-\s().]*$/.test(value)) {
                      update({ signatoryMobile: value });
                    }
                  }}
                />
              </Field>
            </div>
          </div>
          <div className="sd-fieldset">
            <span className="sd-fieldset-title">Spanidea</span>
            <div className="sd-row">
              <Field label="Business unit" required error={errors.businessUnit}>
                <Select
                  showSearch
                  size="large"
                  placeholder="Choose a business unit"
                  value={values.businessUnit}
                  onChange={(businessUnit) => update({ businessUnit })}
                  filterOption={labelFilter}
                  options={businessUnits}
                  status={errors.businessUnit ? 'error' : ''}
                />
              </Field>
              <Field label="Business owner" required error={errors.businessOwner}>
                <Select
                  showSearch
                  size="large"
                  placeholder="Choose a person"
                  value={values.businessOwner}
                  onChange={(businessOwner) => update({ businessOwner })}
                  filterOption={labelFilter}
                  options={employees}
                  status={errors.businessOwner ? 'error' : ''}
                />
              </Field>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'document',
      title: 'Document',
      content: (
        <div className="sd-form">
          <FileDrop
            file={file}
            existingName={existingFileName}
            onChange={(picked) => {
              setFile(picked);
              setErrors((prev) => ({ ...prev, file: undefined }));
            }}
            error={errors.file}
            label="Signed MSA"
          />
        </div>
      ),
    },
    {
      key: 'review',
      title: 'Review',
      content: (
        <div className="sd-form">
          <Summary
            rows={[
              { label: 'Contracting entity', value: entityName(values.seId) },
              { label: 'Client', value: values.clientLabel },
              { label: 'Start date', value: formatDate(values.start) },
              {
                label: 'End date',
                value: values.endless ? 'No end date' : formatDate(values.end),
              },
              {
                label: 'Client signatory',
                value: [values.signatoryName, values.signatoryEmail, values.signatoryMobile]
                  .filter(Boolean)
                  .join(' · '),
              },
              { label: 'Business unit', value: unitLabel },
              { label: 'Business owner', value: ownerLabel },
              { label: 'Document', value: file?.name || existingFileName },
            ]}
          />
          {canSendForApproval && (
            <span className="sd-help">
              Sending for approval moves the MSA to Pending approval. You can also keep it as a draft.
            </span>
          )}
        </div>
      ),
    },
  ];

  const draftLabel = isEdit ? 'Save changes' : 'Save as draft';

  return (
    <StepDrawer
      open={open}
      title={isEdit ? 'Edit MSA' : 'New MSA'}
      subtitle={values.clientLabel || 'Master service agreement'}
      steps={steps}
      current={step}
      onStepClick={goToStep}
      onBack={() => setStep(step - 1)}
      onNext={goNext}
      onClose={() => onClose(false)}
      footerStart={
        canSendForApproval && (
          <Button
            onClick={() => save(STATUS.draft)}
            loading={saving === 'draft'}
            disabled={!!saving}
          >
            {draftLabel}
          </Button>
        )
      }
      submitButton={
        canSendForApproval ? (
          <Button
            type="primary"
            onClick={() => save(STATUS.pending)}
            loading={saving === 'approval'}
            disabled={!!saving}
          >
            Send for approval
          </Button>
        ) : (
          <Button
            type="primary"
            onClick={() => save(STATUS.draft)}
            loading={saving === 'draft'}
            disabled={!!saving}
          >
            {draftLabel}
          </Button>
        )
      }
    />
  );
};

export default MsaDrawer;
